import useRequestPublicationMutation from '../../hooks/useRequestPublicationMutation';
import useWithdrawKlydoMutation from '../../hooks/useWithdrawKlydoMutation';
import { KlydoReviewStatus, PopulatedKlydo } from '../../../../api/klydo';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PROFILE_MANDATORY_FIELDS_TO_SUBMIT_KLYDO } from '../../../../consts/mandatoryFields';
import useAppContext from '../../../../hooks/useAppContext';
import useDeviceType from '../../../../hooks/useDeviceType';
import { AlertType } from '../../../../design-system/DSAlert';
import {
  isKlydoDataIncomplete,
  getKlydoMissingFieldsMessage,
  getPublicUnsupportedProperties,
} from '../../../../utils/klydoValidation';

type UseKlydoCardProps = {
  klydo: PopulatedKlydo;
  handleDeleteKlydo?: (klydoId: string) => void;
  handleRemoveKlydoFromCollection?: (klydoId: string) => void;
};
const useKlydoCard = ({
  klydo,
  handleDeleteKlydo,
  handleRemoveKlydoFromCollection,
}: UseKlydoCardProps) => {
  const { user, showAlert, isAdmin } = useAppContext();
  const navigate = useNavigate();
  const [warnningDialogOpen, setWarningDialogOpen] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState<boolean>(false);
  const [specialPropertiesDialogOpen, setSpecialPropertiesDialogOpen] =
    useState<boolean>(false);

  const { mutate: requestPublicationMutation } = useRequestPublicationMutation({
    onError: (error) => {
      showAlert({
        message: error.response?.data?.message || error.message,
        alertType: AlertType.ERROR,
      });
    },
  });
  const { mutate: withdrawKlydoMutation } = useWithdrawKlydoMutation({
    onError: (error) => {
      showAlert({
        message: error.response?.data?.message || error.message,
        alertType: AlertType.ERROR,
      });
    },
  });

  const [
    profileIncompleteErrorDialogOpen,
    setProfileIncompleteErrorDialogOpen,
  ] = useState<boolean>(false);

  const isProfileMissingFields = PROFILE_MANDATORY_FIELDS_TO_SUBMIT_KLYDO.some(
    (field) => !user?.[field],
  );

  const klydoDataIncomplete = useMemo(
    () => isKlydoDataIncomplete(klydo),
    [klydo],
  );

  const getMissingFieldsTooltip = useMemo(
    () => getKlydoMissingFieldsMessage(klydo),
    [klydo],
  );

  const [klydoIncompleteErrorDialogOpen, setKlydoIncompleteErrorDialogOpen] =
    useState<boolean>(false);

  const handleCloseErrorDialog = useCallback(() => {
    setProfileIncompleteErrorDialogOpen(false);
  }, [setProfileIncompleteErrorDialogOpen]);

  // Server state is the whole truth here — nothing on the card is editable.
  const publicUnsupportedProperties = useMemo(
    () =>
      getPublicUnsupportedProperties({
        hasChime: !!klydo.chimesUrls?.[0],
        hasStrike: !!klydo.strikesUrls?.[0],
        soundMode: klydo.capabilitiesV2?.animationSound?.mode,
      }),
    [klydo],
  );

  const handleSubmitKlydo = useCallback(() => {
    if (klydoDataIncomplete) {
      setKlydoIncompleteErrorDialogOpen(true);
      return;
    }
    if (isProfileMissingFields && !isAdmin) {
      setProfileIncompleteErrorDialogOpen(true);
      return;
    }
    // The api strips these on approval either way, so the artist is told first.
    if (publicUnsupportedProperties.length) {
      setSpecialPropertiesDialogOpen(true);
      return;
    }
    setSubmitDialogOpen(true);
  }, [
    klydoDataIncomplete,
    isProfileMissingFields,
    isAdmin,
    publicUnsupportedProperties.length,
  ]);

  const onConfirmSpecialProperties = useCallback(() => {
    setSpecialPropertiesDialogOpen(false);
    setSubmitDialogOpen(true);
  }, []);

  const onClosedSpecialPropertiesDialog = useCallback(() => {
    setSpecialPropertiesDialogOpen(false);
  }, []);

  // Nothing on the gallery card is editable, so `klydo` here is server state
  // straight out of the klydosList query — there is never unsaved content for the
  // api to miss when it validates the stored klydo.
  const onApprovedSubmit = useCallback(() => {
    requestPublicationMutation(
      {
        klydoId: klydo.id,
        // Quick-submit from the card publishes immediately once approved
        // (the detail-view modal is where scheduling is chosen).
        publicationTimestamp: Date.now(),
        // Without the stored reply the api resubmits in silence, clearing both it
        // and the reviewer's note.
        comment: klydo.public?.reviewStatus?.comment,
      },
      {
        onSuccess: () => setSubmitDialogOpen(false),
      },
    );
  }, [
    klydo.id,
    klydo.public?.reviewStatus?.comment,
    requestPublicationMutation,
  ]);

  // "Switch to draft": clear the public review axis so the klydo becomes
  // editable again.
  const handleCancelReview = (klydo: PopulatedKlydo) => {
    withdrawKlydoMutation({ klydoId: klydo.id });
  };

  const isInCollectionKlydo = !!klydo.collectionId;

  const menuButtonItemsList = useMemo(() => {
    const menuItems = [];

    if (handleRemoveKlydoFromCollection) {
      menuItems.push({
        title: 'Remove',
        isDisabled: false,
        onClick: () => handleRemoveKlydoFromCollection(klydo.id),
      });
    }
    const isApproved =
      klydo.public?.reviewStatus?.state === KlydoReviewStatus.Approved;
    // A klydo that belongs to a collection can't be deleted directly — it would
    // orphan its id in collection.klydoIds (the API blocks this too). Remove it
    // from the collection first.
    const isInCollection = !!klydo.collectionId;
    if (handleDeleteKlydo) {
      menuItems.push({
        title: 'Delete',
        isDisabled: isApproved || isInCollection,
        tooltip: isInCollection
          ? 'Remove this Klydo from its collection before deleting'
          : undefined,
        onClick: () => handleDeleteKlydo(klydo.id),
      });
    }
    menuItems.push(
      klydo.public?.reviewStatus?.state === KlydoReviewStatus.Pending
        ? {
            title: 'Switch to draft',
            isDisabled: false,
            onClick: () => handleCancelReview(klydo),
          }
        : {
            title: 'Make Public',
            // Keyed on the verdict, not on the record existing, so the card agrees
            // with the detail page. A migrated draft carries a record with no
            // `state` and has never been submitted.
            //
            // A klydo in a collection is reviewed with that collection and must
            // never take the Feed route: approval strips its chime, strike and
            // audio, which would break the collection's sound consistency (see
            // CollectionsMonitor). The editor CTA already refuses this; the card
            // was the way around it.
            isDisabled:
              !!klydo.public?.reviewStatus?.state || isInCollectionKlydo,
            onClick: () => handleSubmitKlydo(),
            tooltip: isInCollectionKlydo
              ? 'This Klydo is reviewed with its Collection.'
              : klydoDataIncomplete
                ? getMissingFieldsTooltip
                : undefined,
          },
      {
        title: 'Edit',
        isDisabled: isApproved,
        onClick: () => navigate(`/klydo/${klydo.id}`),
      },
    );
    return menuItems;
  }, [
    klydo,
    isInCollectionKlydo,
    handleSubmitKlydo,
    handleRemoveKlydoFromCollection,
    handleDeleteKlydo,
  ]);

  const onEditProfile = () => {
    navigate('/artist-page?edit=true');
  };

  const onClosedSubmitDialog = useCallback(() => {
    setSubmitDialogOpen(false);
  }, []);

  const { isMobile } = useDeviceType();
  const isEditAvailable = !isMobile;

  const handleKlydoCardClick = useCallback(() => {
    if (isEditAvailable) {
      navigate(`/klydo/${klydo.id}`);
    }
  }, [isEditAvailable, klydo.id, navigate]);

  return {
    menuButtonItemsList,
    setWarningDialogOpen,
    warnningDialogOpen,
    profileIncompleteErrorDialogOpen,
    handleCloseErrorDialog,
    onEditProfile,
    onClosedSubmitDialog,
    submitDialogOpen,
    onApprovedSubmit,
    publicUnsupportedProperties,
    specialPropertiesDialogOpen,
    onConfirmSpecialProperties,
    onClosedSpecialPropertiesDialog,
    isEditAvailable,
    handleKlydoCardClick,
    klydoIncompleteErrorDialogOpen,
  };
};

export default useKlydoCard;
